class ArticleToc extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const content = document.getElementById(this.dataset.contentId);
    if (!content) return;

    const headings = content.querySelectorAll('h2, h3');
    if (headings.length < 2) { this.remove(); return; }

    this._buildToc(headings);
    this._initMainToggle();
    this._initSubToggles();
    this._initScrollspy(headings);

    // Move TOC just before the first heading in the content
    const firstHeading = content.querySelector('h2, h3');
    if (firstHeading) firstHeading.insertAdjacentElement('beforebegin', this);
  }

  _buildToc(headings) {
    const list = this.querySelector('.article-toc-list');
    if (!list) return;

    // Group H3s under their nearest H2
    const groups = [];
    let current = null;

    headings.forEach((heading, i) => {
      const id = `toc-heading-${i}`;
      heading.id = id;

      if (heading.tagName === 'H2') {
        current = { heading, id, children: [] };
        groups.push(current);
      } else {
        if (current) {
          current.children.push({ heading, id });
        } else {
          groups.push({ heading, id, children: [] });
        }
      }
    });

    groups.forEach((group, gi) => {
      const li = document.createElement('li');
      li.className = 'article-toc-item';

      if (group.children.length > 0) {
        const subId = `toc-sub-${gi}`;
        li.innerHTML = `
          <div class="article-toc-row">
            <a class="article-toc-link" href="#${group.id}">${group.heading.textContent}</a>
            <button class="article-toc-sub-toggle" aria-expanded="false" aria-controls="${subId}" aria-label="Toggle sub-sections">
              <svg class="article-toc-arrow" width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="article-toc-sub-body" id="${subId}" aria-hidden="true">
            <ul class="article-toc-sublist">
              ${group.children.map(c => `
                <li class="article-toc-sub-item">
                  <a class="article-toc-link article-toc-link-child" href="#${c.id}">${c.heading.textContent}</a>
                </li>
              `).join('')}
            </ul>
          </div>`;
      } else {
        li.innerHTML = `<a class="article-toc-link" href="#${group.id}">${group.heading.textContent}</a>`;
      }

      list.appendChild(li);
    });
  }

  _initMainToggle() {
    const toggle = this.querySelector('.article-toc-toggle');
    const body = this.querySelector('.article-toc-body');
    if (!toggle || !body) return;

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      body.setAttribute('aria-hidden', String(open));
    });
  }

  _initSubToggles() {
    this.querySelectorAll('.article-toc-sub-toggle').forEach(btn => {
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        const subBody = document.getElementById(btn.getAttribute('aria-controls'));
        if (subBody) subBody.setAttribute('aria-hidden', String(open));
      });
    });
  }

  _initScrollspy(headings) {
    const links = this.querySelectorAll('.article-toc-link');
    let userHasScrolled = false;

    window.addEventListener('scroll', () => { userHasScrolled = true; }, { once: true, passive: true });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          links.forEach((l) => l.classList.remove('is-active'));
          const active = this.querySelector(`.article-toc-link[href="#${entry.target.id}"]`);
          if (!active) return;
          active.classList.add('is-active');

          // Auto-open the sub-body containing an active H3 only after user scrolls
          if (!userHasScrolled) return;
          const subBody = active.closest('.article-toc-sub-body');
          if (subBody && subBody.getAttribute('aria-hidden') === 'true') {
            subBody.setAttribute('aria-hidden', 'false');
            const btn = this.querySelector(`[aria-controls="${subBody.id}"]`);
            if (btn) btn.setAttribute('aria-expanded', 'true');
          }
        });
      },
      { rootMargin: '-20% 0px -75% 0px' }
    );

    headings.forEach((h) => observer.observe(h));
  }
}

customElements.define('article-toc', ArticleToc);
